/**
 * Banco de Palabras (corpus PEFF) — vista SOLO LECTURA. El corpus alimenta la
 * planificación de batería y el scoring (regla de oro #3): aquí se navega y
 * filtra, no se edita. Cubre las CUATRO tablas del corpus (todas administrables):
 * sílabas (F1), palabras fonéticas (F2/F3), oraciones (F4) y PSF (PSF1/PSF2).
 * Filtros: batería, ejercicio, proceso fonológico, edad, estado y cobertura de medios.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { adminApi } from "../../api/endpoints.js";
import { useQuery } from "../../hooks/useQuery.js";
import type { CorpusTipoMedia, EstadoCorpus, TipoBateriaCorpus } from "../../api/types.js";
import { Async } from "./ui.js";
import { CATALOGO_PROCESOS, CATEGORIA_LABEL, nombreProceso, type CategoriaProceso } from "./procesos.js";
import s from "./admin.module.css";

const LIMIT = 50;
const CATEGORIAS: CategoriaProceso[] = ["estructural", "asimilacion", "sustitucion"];

/** Etiqueta de ejercicio por tabla del corpus (word_media.corpus_tipo). */
const EJERCICIO_LABEL: Record<CorpusTipoMedia, string> = {
  silaba: "F1 · sílabas",
  fonetica_palabra: "F2/F3 · denominación",
  oracion: "F4 · oraciones",
  psf: "PSF1/PSF2 · fonológica",
};

function TipoBadge({ tipo }: { tipo: TipoBateriaCorpus }) {
  const cls = tipo === "fonologica" ? s.badgePurple : s.badgeBlue;
  return <span className={`${s.badge} ${cls}`}>{tipo === "fonologica" ? "Fonológica" : "Fonética"}</span>;
}

function EstadoCorpusBadge({ estado }: { estado: EstadoCorpus }) {
  return estado === "revision" ? (
    <span className={`${s.badge} ${s.badgeAmber}`}>Revisión clínica</span>
  ) : (
    <span className={`${s.badge} ${s.badgeGreen}`}>Activo</span>
  );
}

export function AdminBanco() {
  const navigate = useNavigate();
  const [tipo, setTipo] = useState<"" | TipoBateriaCorpus>("");
  const [ejercicio, setEjercicio] = useState<"" | CorpusTipoMedia>("");
  const [proceso, setProceso] = useState("");
  const [edad, setEdad] = useState("");
  const [estado, setEstado] = useState<"" | EstadoCorpus>("");
  const [medios, setMedios] = useState<"" | "sinImagen" | "sinAudio">("");
  const [offset, setOffset] = useState(0);

  const q = useQuery(
    () =>
      adminApi.wordBankCorpus({
        ...(tipo ? { tipoBateria: tipo } : {}),
        ...(ejercicio ? { corpusTipo: ejercicio } : {}),
        ...(proceso ? { proceso } : {}),
        ...(edad ? { edad: Number(edad) } : {}),
        ...(estado ? { estado } : {}),
        ...(medios === "sinImagen" ? { sinImagen: true } : {}),
        ...(medios === "sinAudio" ? { sinAudio: true } : {}),
        limit: LIMIT,
        offset,
      }),
    [tipo, ejercicio, proceso, edad, estado, medios, offset],
  );

  function cambiarFiltro(fn: () => void) {
    setOffset(0);
    fn();
  }

  const total = q.data?.total ?? 0;

  return (
    <>
      <div className={s.sectionTitle}>Banco de Palabras</div>
      <p className={s.muted} style={{ marginTop: -4, marginBottom: 12 }}>
        Corpus clínico PEFF (solo lectura). La edición del corpus exige una capa de validación clínica
        y llega en un PR aparte.
      </p>

      <div className={s.toolbar}>
        <select
          className={s.select}
          value={tipo}
          onChange={(e) => cambiarFiltro(() => setTipo(e.target.value as "" | TipoBateriaCorpus))}
        >
          <option value="">Toda la batería</option>
          <option value="fonologica">Fonológica (PSF)</option>
          <option value="fonetica">Fonética (F1–F4)</option>
        </select>
        <select
          className={s.select}
          value={ejercicio}
          onChange={(e) => cambiarFiltro(() => setEjercicio(e.target.value as "" | CorpusTipoMedia))}
        >
          <option value="">Todo ejercicio</option>
          <option value="silaba">{EJERCICIO_LABEL.silaba}</option>
          <option value="fonetica_palabra">{EJERCICIO_LABEL.fonetica_palabra}</option>
          <option value="oracion">{EJERCICIO_LABEL.oracion}</option>
          <option value="psf">{EJERCICIO_LABEL.psf}</option>
        </select>
        <select
          className={s.select}
          value={proceso}
          onChange={(e) => cambiarFiltro(() => setProceso(e.target.value))}
        >
          <option value="">Todos los procesos</option>
          {CATEGORIAS.map((cat) => (
            <optgroup key={cat} label={CATEGORIA_LABEL[cat]}>
              {CATALOGO_PROCESOS.filter((p) => p.categoria === cat).map((p) => (
                <option key={p.codigo} value={p.codigo}>
                  {p.codigo} · {p.nombre}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
        <select
          className={s.select}
          value={edad}
          onChange={(e) => cambiarFiltro(() => setEdad(e.target.value))}
        >
          <option value="">Toda edad</option>
          {[3, 4, 5, 6, 7, 8].map((n) => (
            <option key={n} value={n}>
              {n} años
            </option>
          ))}
        </select>
        <select
          className={s.select}
          value={estado}
          onChange={(e) => cambiarFiltro(() => setEstado(e.target.value as "" | EstadoCorpus))}
        >
          <option value="">Todos los estados</option>
          <option value="activo">Activo</option>
          <option value="revision">Revisión clínica</option>
        </select>
        <select
          className={s.select}
          value={medios}
          onChange={(e) => cambiarFiltro(() => setMedios(e.target.value as "" | "sinImagen" | "sinAudio"))}
        >
          <option value="">Todos los medios</option>
          <option value="sinImagen">Sin imagen</option>
          <option value="sinAudio">Sin audio</option>
        </select>
        <span className={s.count}>{total} ítems</span>
      </div>

      <div className={s.panel}>
        <Async q={q}>
          {(data) => (
            <div className={s.tableWrap}>
              <table className={s.table}>
                <thead>
                  <tr>
                    <th>Ítem</th>
                    <th>Ejercicio</th>
                    <th>Batería</th>
                    <th>Proceso</th>
                    <th>Posición</th>
                    <th>Edad</th>
                    <th>Banda</th>
                    <th>Estado</th>
                    <th>Medios</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((it) => (
                    <tr
                      key={it.id}
                      className={s.rowClickable}
                      onClick={() => navigate(`/admin/banco/${encodeURIComponent(it.id)}`)}
                    >
                      <td>{it.palabra}</td>
                      <td className={s.muted}>{EJERCICIO_LABEL[it.corpusTipo]}</td>
                      <td>
                        <TipoBadge tipo={it.tipoBateria} />
                      </td>
                      <td className={s.muted}>
                        {it.procesoPrincipal ? nombreProceso(it.procesoPrincipal) : "—"}
                      </td>
                      <td className={s.mono}>{it.posicion ?? "—"}</td>
                      <td className={s.muted}>{it.edadExigencia ?? "—"}</td>
                      <td>{it.banda ?? <span className={s.muted}>—</span>}</td>
                      <td>
                        <EstadoCorpusBadge estado={it.estado} />
                      </td>
                      <td>
                        <span
                          className={`${s.badge} ${it.tieneImagen ? s.badgeGreen : s.badgeNeutral}`}
                          title={it.tieneImagen ? "con imagen" : "sin imagen"}
                        >
                          🖼
                        </span>{" "}
                        <span
                          className={`${s.badge} ${it.tieneAudio ? s.badgeGreen : s.badgeNeutral}`}
                          title={it.tieneAudio ? "con audio" : "sin audio"}
                        >
                          🔊
                        </span>
                      </td>
                    </tr>
                  ))}
                  {data.items.length === 0 ? (
                    <tr>
                      <td colSpan={9} className={s.empty}>
                        Sin ítems con esos filtros.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          )}
        </Async>
      </div>

      <div className={s.toolbar} style={{ marginTop: 12, justifyContent: "flex-end" }}>
        <button className={s.btn} disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - LIMIT))}>
          ‹ Anteriores
        </button>
        <span className={s.count}>
          {total === 0 ? 0 : offset + 1}–{Math.min(offset + LIMIT, total)} de {total}
        </span>
        <button className={s.btn} disabled={offset + LIMIT >= total} onClick={() => setOffset(offset + LIMIT)}>
          Siguientes ›
        </button>
      </div>
    </>
  );
}
